import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE } from "@/components/pageSettings/AdminModal";

const GROUPS = [
  ["broken", "Live with a problem", "border-l-red-600"],
  ["not_live", "Not live yet", "border-l-amber-500"],
  ["not_started", "No buyer page yet", "border-l-zinc-400"],
];

function Row({ r }) {
  const started = r.group !== "not_started";
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-3 py-2" data-testid={`setup-progress-row-${r.org_id}`}>
      <span className="font-bold text-sm min-w-0 sm:w-56 truncate" title={r.name}>{r.name}</span>
      <span className="text-[10px] uppercase tracking-widest border border-black px-1.5 py-0.5 self-start sm:self-auto whitespace-nowrap"
            data-testid={`setup-progress-step-${r.org_id}`}>
        Step {r.step_no} of 5 · {r.label}
      </span>
      <span className="flex-1 min-w-0 text-xs text-zinc-600" data-testid={`setup-progress-note-${r.org_id}`}>
        {started ? r.note : "Needs a buyer page before any page can send them leads"}
      </span>
      <Link to={`/clients/${r.org_id}/setup?step=${r.step}`} className={`${started ? BTN_BLACK : BTN_WHITE} self-start sm:self-auto`}
            data-testid={`setup-progress-continue-${r.org_id}`}>
        {started ? (r.group === "broken" ? "Fix" : "Continue") : "Start setup"} <ArrowRight size={10} weight="bold" />
      </Link>
    </li>
  );
}

function Group({ g, label, edge, rows }) {
  if (!rows.length) return null;
  return (
    <div className={`border-2 border-black border-l-8 ${edge} bg-white`} data-testid={`setup-progress-group-${g}`}>
      <div className="px-3 py-1.5 border-b border-zinc-200 label-mono flex items-center gap-1">
        {g === "broken" && <WarningCircle size={12} weight="fill" className="text-red-600" />}
        {label} ({rows.length})
      </div>
      <ul className="divide-y divide-zinc-200 max-h-96 overflow-y-auto">{rows.map((r) => <Row key={r.org_id} r={r} />)}</ul>
    </div>
  );
}

// Clients page: every buyer that isn't live (or is live with a broken step) and where each one is stuck.
export function SetupProgressList() {
  const [d, setD] = useState(null);
  const [showNew, setShowNew] = useState(false);
  useEffect(() => {
    api.get("/admin/buyer-setup/progress").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  const { counts } = d;
  const total = counts.broken + counts.not_live;
  const by = (g) => d.rows.filter((r) => r.group === g);
  return (
    <section className="brut-card p-3 md:p-4 mb-4 space-y-3" data-testid="setup-progress-list">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-chivo font-black uppercase tracking-tighter text-base md:text-lg">Buyers not live yet</h2>
        <span className="label-mono text-zinc-600" data-testid="setup-progress-counts">
          {counts.not_live} not live · {counts.broken} live with a problem
        </span>
        {counts.not_started > 0 && (
          <button type="button" className={`${BTN_WHITE} ml-auto`} onClick={() => setShowNew((v) => !v)} data-testid="setup-progress-toggle-not-started">
            {showNew ? "Hide" : "Show"} {counts.not_started} with no buyer page
          </button>
        )}
      </div>
      {!total && !showNew && <p className="text-xs text-zinc-600" data-testid="setup-progress-empty">Every buyer with a page is live and set up.</p>}
      {GROUPS.filter(([g]) => g !== "not_started" || showNew).map(([g, label, edge]) => (
        <Group key={g} g={g} label={label} edge={edge} rows={by(g)} />
      ))}
    </section>
  );
}
